/**
 * Analytics API Service — wraps all /v1/analytics/* endpoints using the
 * existing api-client pattern. Handles 503 (analytics unavailable) with
 * a user-friendly error message.
 *
 * Requirements: 8.1–8.14, 11.6
 */

import * as api from './api-client';
import type { ApiErrorResponse } from './api-client';
import type {
  AnalyticsDashboard,
  AnalyticsWidget,
  WidgetLayoutEntry,
  QueryPayload,
  QueryResultRow,
  DrillDownPayload,
  DrillDownCard,
  QueryFilter,
  ChartType,
  Dimension,
  Measure,
} from '@coreflow/shared';

// ---------------------------------------------------------------------------
// Error Types
// ---------------------------------------------------------------------------

/**
 * Thrown when the Analytics Store is unavailable (503).
 * Contains a user-friendly message suitable for display in the UI.
 */
export class AnalyticsUnavailableError extends Error {
  public readonly statusCode = 503;
  public readonly retryAfterSeconds = 30;

  constructor() {
    super('Analytics is temporarily unavailable. Please try again in 30 seconds.');
    this.name = 'AnalyticsUnavailableError';
  }
}

// ---------------------------------------------------------------------------
// Response Types
// ---------------------------------------------------------------------------

export interface DashboardListResponse {
  readonly dashboards: AnalyticsDashboard[];
  readonly nextCursor: string | null;
}

export interface SharedDashboard extends AnalyticsDashboard {
  readonly ownerDisplayName: string;
}

export interface SharedDashboardListResponse {
  readonly dashboards: SharedDashboard[];
  readonly nextCursor: string | null;
}

export interface DrillDownResponse {
  readonly cards: DrillDownCard[];
  readonly nextCursor: string | null;
}

export interface PdfExportResponse {
  readonly downloadUrl: string;
}

export interface CreateDashboardBody {
  readonly title?: string;
  readonly visibility?: 'private' | 'public-read';
}

export interface UpdateDashboardBody {
  readonly title?: string;
  readonly visibility?: 'private' | 'public-read';
  readonly widgetLayout?: WidgetLayoutEntry[];
  readonly version: number;
}

export interface CreateWidgetBody {
  readonly title: string;
  readonly chartType: ChartType;
  readonly dataSourceBoardIds: string[];
  readonly dimension: Dimension;
  readonly measure: Measure;
  readonly filters?: QueryFilter[];
}

export interface UpdateWidgetBody {
  readonly title?: string;
  readonly chartType?: ChartType;
  readonly dataSourceBoardIds?: string[];
  readonly dimension?: Dimension;
  readonly measure?: Measure;
  readonly filters?: QueryFilter[];
  readonly version: number;
}

// ---------------------------------------------------------------------------
// Internal Helpers
// ---------------------------------------------------------------------------

/**
 * Wraps an API call and converts 503 responses into AnalyticsUnavailableError.
 */
async function withAnalyticsErrorHandling<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error: unknown) {
    if (isApiError(error) && error.statusCode === 503) {
      throw new AnalyticsUnavailableError();
    }
    throw error;
  }
}

function isApiError(error: unknown): error is ApiErrorResponse {
  if (!error || typeof error !== 'object') return false;
  return (error as Record<string, unknown>).type === 'api';
}

// ---------------------------------------------------------------------------
// Dashboard Endpoints
// ---------------------------------------------------------------------------

/**
 * Lists dashboards owned by the current user.
 * Supports cursor-based pagination with a default page size of 20.
 */
export async function listMyDashboards(
  cursor?: string,
  limit: number = 20
): Promise<DashboardListResponse> {
  return withAnalyticsErrorHandling(() => {
    const query: Record<string, string> = { limit: String(limit) };
    if (cursor) {
      query.cursor = cursor;
    }
    return api.get<DashboardListResponse>('/v1/analytics/dashboards', query);
  });
}

/**
 * Lists public-read dashboards created by other users.
 * Supports cursor-based pagination with a default page size of 20.
 */
export async function listSharedDashboards(
  cursor?: string,
  limit: number = 20
): Promise<SharedDashboardListResponse> {
  return withAnalyticsErrorHandling(() => {
    const query: Record<string, string> = { limit: String(limit) };
    if (cursor) {
      query.cursor = cursor;
    }
    return api.get<SharedDashboardListResponse>('/v1/analytics/dashboards/shared', query);
  });
}

/**
 * Creates a new dashboard with default title "Untitled Dashboard" and private visibility.
 */
export async function createDashboard(
  body?: CreateDashboardBody
): Promise<AnalyticsDashboard> {
  return withAnalyticsErrorHandling(() => {
    return api.post<AnalyticsDashboard>('/v1/analytics/dashboards', body ?? {});
  });
}

/**
 * Retrieves a single dashboard by ID.
 * Returns the dashboard if the user is the owner or if the dashboard is public-read.
 */
export async function getDashboard(dashboardId: string): Promise<AnalyticsDashboard> {
  return withAnalyticsErrorHandling(() => {
    return api.get<AnalyticsDashboard>(`/v1/analytics/dashboards/${dashboardId}`);
  });
}

/**
 * Updates dashboard metadata (title, visibility, widget layout).
 * Only the dashboard owner can perform this operation.
 */
export async function updateDashboard(
  dashboardId: string,
  body: UpdateDashboardBody
): Promise<AnalyticsDashboard> {
  return withAnalyticsErrorHandling(() => {
    return api.put<AnalyticsDashboard>(`/v1/analytics/dashboards/${dashboardId}`, body);
  });
}

/**
 * Deletes a dashboard and all its widgets.
 * Only the dashboard owner can perform this operation.
 */
export async function deleteDashboard(dashboardId: string): Promise<void> {
  return withAnalyticsErrorHandling(() => {
    return api.del(`/v1/analytics/dashboards/${dashboardId}`);
  });
}

// ---------------------------------------------------------------------------
// Widget Endpoints
// ---------------------------------------------------------------------------

/**
 * Creates a widget on the specified dashboard.
 * Only the dashboard owner can perform this operation.
 */
export async function createWidget(
  dashboardId: string,
  body: CreateWidgetBody
): Promise<AnalyticsWidget> {
  return withAnalyticsErrorHandling(() => {
    return api.post<AnalyticsWidget>(
      `/v1/analytics/dashboards/${dashboardId}/widgets`,
      body
    );
  });
}

/**
 * Updates a widget configuration on the specified dashboard.
 * Only the dashboard owner can perform this operation.
 */
export async function updateWidget(
  dashboardId: string,
  widgetId: string,
  body: UpdateWidgetBody
): Promise<AnalyticsWidget> {
  return withAnalyticsErrorHandling(() => {
    return api.put<AnalyticsWidget>(
      `/v1/analytics/dashboards/${dashboardId}/widgets/${widgetId}`,
      body
    );
  });
}

/**
 * Deletes a widget from the specified dashboard.
 * Only the dashboard owner can perform this operation.
 */
export async function deleteWidget(
  dashboardId: string,
  widgetId: string
): Promise<void> {
  return withAnalyticsErrorHandling(() => {
    return api.del(`/v1/analytics/dashboards/${dashboardId}/widgets/${widgetId}`);
  });
}

// ---------------------------------------------------------------------------
// Query Endpoints
// ---------------------------------------------------------------------------

/**
 * Executes an analytics aggregation query.
 * The BFF filters boardIds based on the user's memberships before execution.
 */
export async function executeQuery(payload: QueryPayload): Promise<QueryResultRow[]> {
  return withAnalyticsErrorHandling(async () => {
    const response = await api.post<{ data: QueryResultRow[] } | QueryResultRow[]>('/v1/analytics/query', payload);
    // BFF wraps results in { data: [...] }
    if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown }).data)) {
      return (response as { data: QueryResultRow[] }).data;
    }
    // Fallback: already unwrapped
    return response as QueryResultRow[];
  });
}

/**
 * Executes a drill-down query into individual cards for a specific dimension value.
 */
export async function executeDrillDown(payload: DrillDownPayload): Promise<DrillDownResponse> {
  return withAnalyticsErrorHandling(async () => {
    const response = await api.post<{ data: DrillDownResponse } | DrillDownResponse>('/v1/analytics/drill-down', payload);
    // BFF wraps results in { data: {...} }
    if (response && typeof response === 'object' && 'data' in response && !('cards' in response)) {
      return (response as { data: DrillDownResponse }).data;
    }
    return response as DrillDownResponse;
  });
}

// ---------------------------------------------------------------------------
// Export Endpoints
// ---------------------------------------------------------------------------

/**
 * Generates and returns CSV data for a specific widget's query results.
 */
export async function exportCsv(dashboardId: string, widgetId: string): Promise<Blob> {
  return withAnalyticsErrorHandling(async () => {
    // CSV export returns raw file data; use the api-client GET which returns JSON.
    // The BFF endpoint returns the CSV as a JSON response with a downloadUrl or inline data.
    // We use the standard GET pattern and let the caller handle download.
    return api.get<Blob>(
      `/v1/analytics/dashboards/${dashboardId}/widgets/${widgetId}/export/csv`
    );
  });
}

/**
 * Initiates PDF generation for the entire dashboard.
 * Returns a presigned S3 URL for download (24-hour expiry).
 */
export async function exportPdf(dashboardId: string): Promise<PdfExportResponse> {
  return withAnalyticsErrorHandling(() => {
    return api.post<PdfExportResponse>(
      `/v1/analytics/dashboards/${dashboardId}/export/pdf`
    );
  });
}
